// ==============================
// SystemFilterBar.js
// (same styling/layout as your earlier SystemFilterBar,
// but control-passing logic follows the "controls/actions" pattern)
// ==============================
import React, { useMemo } from "react";

const normalizeOptions = (options = []) =>
  (Array.isArray(options) ? options : []).map((o) => ({
    value: o.value,
    label: o.label ?? o.title ?? String(o.value ?? ""),
  }));

/**
 * SystemFilterBar
 * - Collapsible advanced-search bar (keeps your existing theme/styling)
 * - Controls passed in via `controls` config array (like the sample FilterBar)
 * - Actions passed in via `actions` config array
 *
 * controls supported:
 *  - { type:"search", value, onChange, placeholder, width }
 *  - { type:"select", value, onChange, options:[{value,label|title}], placeholder, width, title }
 *  - { type:"date", value, onChange, label, iconClass, width }
 *  - { type:"dateRange", labelFrom, labelTo, fromValue, toValue, onChangeFrom, onChangeTo }
 *  - { type:"custom", render: () => ReactNode }
 *
 * actions supported:
 *  - { label, iconClass, onClick, variant:"primary"|"secondary"|"light", disabled }
 */
const SystemFilterBar = ({
  controls = [],
  actions = [],
  collapseId = "systemFilterBar",
}) => {
  const normalizedControls = useMemo(() => (Array.isArray(controls) ? controls : []), [controls]);
  const normalizedActions = useMemo(() => (Array.isArray(actions) ? actions : []), [actions]);

  return (
    <div className="responsive-background">
      <div className="collapse navbar-collapse" id={collapseId}>
        <div className="advanced-search">
          <div className="row align-items-center">
            {/* Controls */}
            {normalizedControls.map((c, idx) => {
              if (c.type === "custom" && typeof c.render === "function") {
                return (
                  <div key={idx} className={c.colClassName || "col-md-3"}>
                    {c.render()}
                  </div>
                );
              }

              if (c.type === "dateRange") {
                return (
                  <div key={idx} className={c.colClassName || "col-md-6"}>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group mb-lg-0">
                          <label className="">{c.labelFrom || "From :"}</label>
                          <div className="input-group">
                            <div className="input-group-prepend">
                              <div className="input-group-text">
                                <i className={`fe fe-calendar lh--9 op-6`}></i>
                              </div>
                            </div>
                            <input
                              className="form-control fc-datepicker"
                              type="date"
                              value={c.fromValue ?? ""}
                              onChange={c.onChangeFrom}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group mb-lg-0">
                          <label className="">{c.labelTo || "To :"}</label>
                          <div className="input-group">
                            <div className="input-group-prepend">
                              <div className="input-group-text">
                                <i className={`fe fe-calendar lh--9 op-6`}></i>
                              </div>
                            </div>
                            <input
                              className="form-control fc-datepicker"
                              type="date"
                              value={c.toValue ?? ""}
                              onChange={c.onChangeTo}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              if (c.type === "date") {
                return (
                  <div key={idx} className={c.colClassName || "col-md-3"}>
                    <div className="form-group mb-lg-0">
                      <label className="">{c.label || "Date"}</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className={`${c.iconClass || "fe fe-calendar lh--9 op-6"}`}></i>
                          </div>
                        </div>
                        <input
                          className="form-control fc-datepicker"
                          type="date"
                          value={c.value ?? ""}
                          onChange={c.onChange}
                        />
                      </div>
                    </div>
                  </div>
                );
              }

              if (c.type === "search") {
                return (
                  <div key={idx} className={c.colClassName || "col-md-3"}>
                    <div className="form-group mb-lg-0">
                      <label className="">{c.label || "Keyword"}</label>
                      <input
                        className="form-control"
                        placeholder={c.placeholder ?? "Search keyword"}
                        type="text"
                        value={c.value ?? ""}
                        onChange={c.onChange}
                        onKeyDown={(e) => e.key === "Enter" && c.onEnter?.(e)}
                      />
                    </div>
                  </div>
                );
              }

              if (c.type === "select") {
                const opts = normalizeOptions(c.options);
                return (
                  <div key={idx} className={c.colClassName || "col-md-3"}>
                    <div className="form-group mb-lg-0">
                      <label className="">{c.title || c.label || c.placeholder || "Select"}</label>
                      <select
                        className="form-control select2-flag-search"
                        value={String(c.value ?? "")}
                        onChange={c.onChange}
                      >
                        {c.placeholder !== undefined && <option value="">{c.placeholder}</option>}
                        {opts.map((o, i) => (
                          <option key={i} value={String(o.value)}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              }

              return null;
            })}
          </div>

          <hr />

          {/* Actions */}
          {normalizedActions.length > 0 && (
            <div className="text-right">
              {normalizedActions.map((a, idx) => {
                const variant = a.variant || "primary";
                const btnClass =
                  variant === "primary"
                    ? "btn btn-primary"
                    : variant === "secondary"
                    ? "btn btn-secondary"
                    : "btn btn-light";

                return (
                  <a
                    key={idx}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      a.onClick?.(e);
                    }}
                    className={`${btnClass} ${idx > 0 ? "ml-2" : ""}`}
                    data-toggle={a.toggleCollapse ? "collapse" : undefined}
                    data-target={a.toggleCollapse ? `#${collapseId}` : undefined}
                    aria-controls={collapseId}
                    aria-expanded="false"
                    aria-label="Toggle navigation"
                    disabled={a.disabled ? true : undefined}
                    style={a.disabled ? { pointerEvents: "none", opacity: 0.6 } : undefined}
                  >
                    {a.iconClass ? <i className={`${a.iconClass} mr-2`}></i> : null}
                    {a.label}
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemFilterBar;
